/**
 * The actions every studio sound offers (rename, share, add to a beat / brief, add to a collection, download, delete)
 * and the dialogs they open. A list calls the hook once, hands the handlers to its rows and renders `dialogs` once.
 * Inside a collection view (`collectionId`) the rows also get "Remove from collection". When the team has brands, a
 * sound's owner can "Set brand…" (POST /track/{id}/update { brand_id }, "" = no brand).
 */
import { useCallback, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { audioStudioApi, downloadAudio, type StudioTrack } from '@/api/audioStudioApi';
import { AddToCollectionDialog } from './AddToCollectionDialog';
import { AudioShareDialog } from './AudioShareDialog';
import { AttachToCampaignDialog } from './AttachToCampaignDialog';
import { BrandPickerDialog, useStudioBrands } from './brands';
import type { AttachableTrack } from './attachPayload';
import { collectionErrorMessage, useCollectionMembership } from './collectionMutations';
import { studioAttachable } from './format';
import { audioKeys } from './queries';

export function useTrackActions({ onDeleted, collectionId }: {
  onDeleted?: (track: StudioTrack) => void;
  /** The list is a collection's: its rows can be taken out of it. */
  collectionId?: string;
} = {}) {
  const qc = useQueryClient();
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [sharing, setSharing] = useState<StudioTrack | null>(null);
  const [attaching, setAttaching] = useState<AttachableTrack | null>(null);
  const [deleting, setDeleting] = useState<StudioTrack | null>(null);
  const [collecting, setCollecting] = useState<StudioTrack | null>(null);
  const [branding, setBranding] = useState<StudioTrack | null>(null);
  const membership = useCollectionMembership();
  const { brands, brandOf } = useStudioBrands();

  const setBrand = useMutation({
    mutationFn: ({ track, brandId }: { track: StudioTrack; brandId: string }) => audioStudioApi.updateTrack(track.track_id, { brand_id: brandId }),
    onSuccess: (_r, { brandId }) => {
      toast.success(brandId ? `Brand set to ${brandOf(brandId)?.brandName ?? 'the brand'}` : 'Brand removed');
      void qc.invalidateQueries({ queryKey: audioKeys.all });
      setBranding(null);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const del = useMutation({
    mutationFn: (t: StudioTrack) => audioStudioApi.deleteTrack(t.track_id),
    onSuccess: (_r, t) => {
      toast.success('Sound deleted');
      void qc.invalidateQueries({ queryKey: audioKeys.tracksAll });
      // A deleted sound leaves every collection too.
      void qc.invalidateQueries({ queryKey: audioKeys.collectionsAll });
      setDeleting(null);
      onDeleted?.(t);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const download = useCallback((t: StudioTrack) => {
    if (!t.audio_url) return;
    void downloadAudio(t.audio_url, t.title || 'sound').catch((e: Error) => toast.error(e.message));
  }, []);

  const dialogs = (
    <>
      <AudioShareDialog track={sharing} open={!!sharing} onOpenChange={(o) => !o && setSharing(null)} onUpdated={(t) => setSharing(t)} />
      <AttachToCampaignDialog track={attaching} open={!!attaching} onOpenChange={(o) => !o && setAttaching(null)} />
      <AddToCollectionDialog track={collecting} open={!!collecting} onOpenChange={(o) => !o && setCollecting(null)} />
      <BrandPickerDialog
        open={!!branding}
        onOpenChange={(o) => !o && setBranding(null)}
        title="Set brand"
        subject={`“${branding?.title || 'Untitled'}”`}
        current={branding?.brand_id}
        busy={setBrand.isPending}
        onPick={(brandId) => { if (branding && brandId !== (branding.brand_id || '')) setBrand.mutate({ track: branding, brandId }); else setBranding(null); }}
      />
      <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete “{deleting?.title}”?</AlertDialogTitle>
            <AlertDialogDescription>The sound is removed for everyone who can see it. This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); if (deleting) del.mutate(deleting); }} className="bg-[#d92d20] text-white hover:bg-[#b42318]">
              {del.isPending && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />} Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );

  return {
    renamingId,
    startRename: (t: StudioTrack) => setRenamingId(t.track_id),
    stopRename: () => setRenamingId(null),
    share: (t: StudioTrack) => setSharing(t),
    attach: (t: StudioTrack) => setAttaching(studioAttachable(t)),
    download,
    addToCollection: (t: StudioTrack) => setCollecting(t),
    removeFromCollection: collectionId
      ? (t: StudioTrack) => membership.mutate(
        { collectionId, trackId: t.track_id, add: false },
        { onSuccess: () => toast.success('Removed from the collection'), onError: (e) => toast.error(collectionErrorMessage(e)) },
      )
      : undefined,
    askDelete: (t: StudioTrack) => setDeleting(t),
    /** Only when the team has brands. */
    setBrand: brands.length ? (t: StudioTrack) => setBranding(t) : undefined,
    dialogs,
  };
}

export type TrackActions = ReturnType<typeof useTrackActions>;
